import { useCallback, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { PortaledHoverHint } from "@/components/ui/title-help";
import { cn } from "@/lib/utils";
import type { CashFlowForecastBucket, CashFlowGranularity } from "./cashFlowApi";

export interface CashFlowChartPeriod {
  periodStart: string;
  periodEnd: string;
  shortLabel: string;
  rangeLabel: string;
  currency: string;
  inflow: number;
  outflow: number;
  net: number;
}

function formatShortChartDate(iso: string): string {
  const parsed = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function subtractCalendarDay(iso: string): string {
  const parsed = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return iso;
  parsed.setDate(parsed.getDate() - 1);
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatShortPeriodLabel(periodStart: string, granularity: CashFlowGranularity): string {
  const parsed = new Date(`${periodStart}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return periodStart;
  if (granularity === "month") {
    return parsed.toLocaleDateString(undefined, { month: "short", year: "2-digit" });
  }
  return parsed.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function formatChartPeriodRangeLabel(
  periodStart: string,
  periodEnd: string,
  granularity: CashFlowGranularity,
): string {
  if (granularity === "month") {
    return formatShortPeriodLabel(periodStart, granularity);
  }
  const lastInclusiveDay = subtractCalendarDay(periodEnd);
  const startLabel = formatShortChartDate(periodStart);
  const endLabel = formatShortChartDate(lastInclusiveDay);
  if (startLabel === endLabel) {
    return startLabel;
  }
  return `${startLabel} – ${endLabel}`;
}

function formatMoneyDetail(amount: number, currency: string): string {
  const resolvedCurrency = currency.trim() === "" ? "USD" : currency;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: resolvedCurrency,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${resolvedCurrency}`;
  }
}

function formatMoneyCompact(amount: number, currency: string): string {
  const abs = Math.abs(amount);
  try {
    const formatted = new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency.trim() === "" ? "USD" : currency,
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(abs);
    return amount < 0 ? `-${formatted}` : formatted;
  } catch {
    const base = abs.toLocaleString(undefined, { maximumFractionDigits: 0 });
    return amount < 0 ? `-${base}` : base;
  }
}

export function buildCashFlowChartPeriods(
  buckets: CashFlowForecastBucket[],
  granularity: CashFlowGranularity,
): CashFlowChartPeriod[] {
  const rows: CashFlowChartPeriod[] = [];
  for (const bucket of buckets) {
    const shortLabel = formatShortPeriodLabel(bucket.period_start, granularity);
    const rangeLabel = formatChartPeriodRangeLabel(bucket.period_start, bucket.period_end, granularity);
    let inflow = 0;
    let outflow = 0;
    let net = 0;
    for (const totals of Object.values(bucket.by_currency)) {
      inflow += totals.inflow;
      outflow += totals.outflow;
      net += totals.net;
    }
    rows.push({
      periodStart: bucket.period_start,
      periodEnd: bucket.period_end,
      shortLabel,
      rangeLabel,
      currency: "USD",
      inflow,
      outflow,
      net,
    });
  }
  return rows;
}

const CHART_HEIGHT = 300;
const CHART_PADDING = { top: 20, right: 24, bottom: 56, left: 76 };

function cashFlowColumnHoverHint(period: CashFlowChartPeriod, currency: string): ReactNode {
  return (
    <span className="block min-w-[10rem] space-y-1.5">
      <span className="block font-semibold text-slate-800">{period.rangeLabel}</span>
      <span className="flex items-baseline justify-between gap-4">
        <span className="text-emerald-700">Inflow</span>
        <span className="font-mono tabular-nums text-slate-800">{formatMoneyDetail(period.inflow, currency)}</span>
      </span>
      <span className="flex items-baseline justify-between gap-4">
        <span className="text-rose-700">Outflow</span>
        <span className="font-mono tabular-nums text-slate-800">{formatMoneyDetail(period.outflow, currency)}</span>
      </span>
      <span className="flex items-baseline justify-between gap-4 border-t border-slate-200 pt-1.5">
        <span className="font-medium text-blue-700">Net</span>
        <span className="font-mono tabular-nums font-semibold text-slate-800">
          {formatMoneyDetail(period.net, currency)}
        </span>
      </span>
    </span>
  );
}

function resolveColumnIndex(
  clientX: number,
  svg: SVGSVGElement,
  periodCount: number,
  plotWidth: number,
  groupWidth: number,
): number {
  const point = svg.createSVGPoint();
  point.x = clientX;
  point.y = 0;
  const inverse = svg.getScreenCTM()?.inverse();
  if (!inverse) {
    return -1;
  }
  const plotX = point.matrixTransform(inverse).x - CHART_PADDING.left;
  if (plotX < 0 || plotX > plotWidth) {
    return -1;
  }
  return Math.min(periodCount - 1, Math.max(0, Math.floor(plotX / groupWidth)));
}

function CurrencyBarChart({ currency, periods }: { currency: string; periods: CashFlowChartPeriod[] }) {
  const columnHintAnchorRef = useRef<HTMLElement | null>(null);
  const columnAnchorRefs = useRef<(SVGRectElement | null)[]>([]);
  const [columnHintIndex, setColumnHintIndex] = useState<number | null>(null);

  const layout = useMemo(() => {
    const maxInflow = Math.max(1, ...periods.map((period) => period.inflow));
    const maxOutflow = Math.max(1, ...periods.map((period) => period.outflow));
    const maxAbsNet = Math.max(1, ...periods.map((period) => Math.abs(period.net)));
    const axisMax = Math.max(maxInflow, maxOutflow, maxAbsNet);

    const plotWidth = Math.max(periods.length * 48, 320);
    const width = plotWidth + CHART_PADDING.left + CHART_PADDING.right;
    const height = CHART_HEIGHT + CHART_PADDING.top + CHART_PADDING.bottom;
    const plotHeight = CHART_HEIGHT;
    const zeroY = CHART_PADDING.top + plotHeight / 2;
    const halfHeight = plotHeight / 2;
    const groupWidth = plotWidth / Math.max(periods.length, 1);
    const barWidth = Math.min(28, groupWidth * 0.55);

    const yScale = (value: number) => zeroY - (value / axisMax) * halfHeight;

    const netPoints = periods.map((period, index) => {
      const x = CHART_PADDING.left + index * groupWidth + groupWidth / 2;
      const y = yScale(period.net);
      return { x, y, period };
    });

    const netLinePath =
      netPoints.length > 0
        ? netPoints.map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`).join(" ")
        : "";

    return {
      axisMax,
      width,
      height,
      plotHeight,
      plotWidth,
      zeroY,
      halfHeight,
      groupWidth,
      barWidth,
      yScale,
      netPoints,
      netLinePath,
    };
  }, [periods]);

  const columnHintPeriod = columnHintIndex != null ? (periods[columnHintIndex] ?? null) : null;

  useLayoutEffect(() => {
    if (columnHintIndex == null) {
      return;
    }
    window.dispatchEvent(new Event("resize"));
  }, [columnHintIndex]);

  const handlePlotPointerMove = useCallback(
    (event: React.PointerEvent<SVGRectElement>) => {
      const svg = event.currentTarget.ownerSVGElement;
      if (!svg) {
        return;
      }
      const index = resolveColumnIndex(
        event.clientX,
        svg,
        periods.length,
        layout.plotWidth,
        layout.groupWidth,
      );
      if (index < 0) {
        columnHintAnchorRef.current = null;
        setColumnHintIndex(null);
        return;
      }
      columnHintAnchorRef.current = columnAnchorRefs.current[index] as HTMLElement | null;
      setColumnHintIndex((previous) => (previous === index ? previous : index));
    },
    [layout.groupWidth, layout.plotWidth, periods.length],
  );

  const handlePlotPointerLeave = useCallback(() => {
    columnHintAnchorRef.current = null;
    setColumnHintIndex(null);
  }, []);

  const yTicks = useMemo(() => {
    const { axisMax } = layout;
    const step = axisMax / 2;
    return [axisMax, step, 0, -step, -axisMax];
  }, [layout]);

  const hasMovement = periods.some((period) => period.inflow > 0 || period.outflow > 0);

  if (!hasMovement) {
    return (
      <p className="text-sm text-slate-500">No scheduled cash movements in this horizon for {currency}.</p>
    );
  }

  return (
    <div className="space-y-2">
      {currency !== "USD" ? (
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{currency}</p>
      ) : null}
      <PortaledHoverHint open={columnHintPeriod != null} anchorRef={columnHintAnchorRef}>
        {columnHintPeriod ? cashFlowColumnHoverHint(columnHintPeriod, currency) : null}
      </PortaledHoverHint>
      <div className="overflow-x-auto pb-1">
        <svg
          viewBox={`0 0 ${layout.width} ${layout.height}`}
          className="min-w-full"
          role="img"
          aria-label={`Cash flow chart for ${currency}`}
        >
          <title>{`Inflows above zero, outflows below, net line by period (${currency})`}</title>

          {yTicks.map((tick) => {
            const y = layout.yScale(tick);
            const isZero = tick === 0;
            return (
              <g key={tick}>
                <line
                  x1={CHART_PADDING.left}
                  x2={layout.width - CHART_PADDING.right}
                  y1={y}
                  y2={y}
                  stroke={isZero ? "#94a3b8" : "#e2e8f0"}
                  strokeWidth={isZero ? 1.5 : 1}
                />
                <text x={CHART_PADDING.left - 8} y={y + 4} textAnchor="end" className="fill-slate-500 text-[10px]">
                  {formatMoneyCompact(tick, currency)}
                </text>
              </g>
            );
          })}

          {periods.map((period, index) => {
            const centerX = CHART_PADDING.left + index * layout.groupWidth + layout.groupWidth / 2;
            const barX = centerX - layout.barWidth / 2;
            const inflowTopY = layout.yScale(period.inflow);
            const inflowHeight = layout.zeroY - inflowTopY;
            const outflowBottomY = layout.yScale(-period.outflow);
            const outflowHeight = outflowBottomY - layout.zeroY;

            return (
              <g key={`${period.periodStart}-${currency}`}>
                {period.inflow > 0 ? (
                  <rect
                    x={barX}
                    y={inflowTopY}
                    width={layout.barWidth}
                    height={Math.max(inflowHeight, 2)}
                    rx={2}
                    className="fill-emerald-500"
                  />
                ) : null}
                {period.outflow > 0 ? (
                  <rect
                    x={barX}
                    y={layout.zeroY}
                    width={layout.barWidth}
                    height={Math.max(outflowHeight, 2)}
                    rx={2}
                    className="fill-rose-500"
                  />
                ) : null}
                <rect
                  ref={(element) => {
                    columnAnchorRefs.current[index] = element;
                  }}
                  x={CHART_PADDING.left + index * layout.groupWidth}
                  y={CHART_PADDING.top}
                  width={layout.groupWidth}
                  height={layout.plotHeight}
                  fill="transparent"
                  pointerEvents="none"
                  aria-hidden
                />
                <text
                  x={centerX}
                  y={layout.height - CHART_PADDING.bottom + 16}
                  textAnchor="end"
                  transform={`rotate(-35, ${centerX}, ${layout.height - CHART_PADDING.bottom + 16})`}
                  className="fill-slate-600 text-[10px]"
                >
                  {period.shortLabel}
                </text>
              </g>
            );
          })}

          {layout.netLinePath !== "" ? (
            <path
              d={layout.netLinePath}
              fill="none"
              stroke="#2563eb"
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
              pointerEvents="none"
            />
          ) : null}

          {layout.netPoints.map((point) => (
            <circle
              key={`net-${point.period.periodStart}`}
              cx={point.x}
              cy={point.y}
              r={3.5}
              className="fill-blue-600 stroke-white"
              strokeWidth={1.5}
              pointerEvents="none"
            />
          ))}

          <rect
            x={CHART_PADDING.left}
            y={CHART_PADDING.top}
            width={layout.plotWidth}
            height={layout.plotHeight}
            fill="transparent"
            className="cursor-help"
            onPointerMove={handlePlotPointerMove}
            onPointerLeave={handlePlotPointerLeave}
          />
        </svg>
      </div>
      <div className="flex flex-wrap items-center gap-4 text-xs text-slate-600">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm bg-emerald-500" aria-hidden />
          Inflow (up)
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm bg-rose-500" aria-hidden />
          Outflow (down)
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-0.5 w-4 rounded bg-blue-600" aria-hidden />
          Net sum
        </span>
      </div>
    </div>
  );
}

export function CashFlowBarChart({
  buckets,
  granularity,
  className,
}: {
  buckets: CashFlowForecastBucket[];
  granularity: CashFlowGranularity;
  className?: string;
}) {
  const periods = useMemo(() => buildCashFlowChartPeriods(buckets, granularity), [buckets, granularity]);

  if (periods.length === 0) {
    return <p className="text-sm text-slate-500">No scheduled cash movements in this horizon.</p>;
  }

  return (
    <div className={cn("space-y-8", className)}>
      <CurrencyBarChart currency="USD" periods={periods} />
    </div>
  );
}
